import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import { Print, Email, ShoppingCart, CheckCircle } from '@mui/icons-material';
import { CartItem } from '../types/CartItem';
import { ontarioTechStyles } from '../theme/ontarioTechTheme';
import { APP_CONFIG } from '../constants/appConstants';

import type { ReceiptPayload } from '../types/ReceiptTypes';

interface ReceiptInterfaceProps {
  cartItems: CartItem[];
  receipt: ReceiptPayload;       // ✅ changed
  onNewTransaction: () => void;
}

const ReceiptInterface: React.FC<ReceiptInterfaceProps> = ({
  cartItems,
  receipt,
  onNewTransaction,
}) => {
  const formatPrice = (price: number) => `$${Number(price || 0).toFixed(2)}`;

  const createdAt = useMemo(() => new Date(receipt.createdAtIso), [receipt.createdAtIso]);

  const handlePrint = () => window.print();

  const handleEmailReceipt = () => {
    // Later: send `receipt` as payload for email template or thermal style email
    alert('Email receipt feature would be implemented here');
  };

  const appliedPromos = useMemo(() => {
    const s = new Set<string>();
    (receipt.appliedPromos || []).forEach((p) => s.add(String(p).toUpperCase()));
    (receipt.items || []).forEach((it) =>
      (it.applied_promos || []).forEach((p) => s.add(String(p).toUpperCase()))
    );
    return Array.from(s);
  }, [receipt.appliedPromos, receipt.items]);

  // prefer receipt items (has promos/discounts); fallback to cartItems
  const displayItems = useMemo(() => {
    if (Array.isArray(receipt.items) && receipt.items.length > 0) {
      return receipt.items.map((ri) => {
        const qty = Math.max(1, ri.qty || 1);
        const lineTotal = Math.max(0, (ri.line_subtotal || 0) - (ri.line_discount || 0));
        return {
          key: ri.item_sku,
          name: ri.name,
          qty,
          unitPrice: Number(ri.unit_price || 0),
          lineTotal,
          lineDiscount: Number(ri.line_discount || 0),
          appliedPromos: Array.isArray(ri.applied_promos) ? ri.applied_promos : [],
        };
      });
    }

    return cartItems.map((ci) => ({
      key: ci.id,
      name: ci.name,
      qty: Math.max(1, ci.quantity || 1),
      unitPrice: Number(ci.price || 0),
      lineTotal: Number(ci.price || 0) * Math.max(1, ci.quantity || 1),
      lineDiscount: 0,
      appliedPromos: [],
    }));
  }, [receipt.items, cartItems]);

  const totals = receipt.totals;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircle sx={{ fontSize: 80, ...ontarioTechStyles.secondaryIcon, mb: 2 }} />
        <Typography
          variant="h3"
          sx={{
            ...ontarioTechStyles.successText,
            fontWeight: 700,
            mb: 1,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Payment Successful!
        </Typography>
        <Typography variant="h6" sx={ontarioTechStyles.receipt.thankYouMessage}>
          Thank you for your purchase
        </Typography>
      </Box>

      {/* Receipt */}
      <Card sx={{ flex: 1, maxHeight: '60vh', overflow: 'auto' }}>
        <CardContent>
          {/* Receipt Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                color: '#2C507A',
                fontWeight: 700,
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Aisle 24
            </Typography>

            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem', lineHeight: 1.6 }}>
              2000 Simcoe Street North<br />
              Oshawa, ON L1H 7K4<br />
              Phone: (905) 721-8668
            </Typography>

            <Divider sx={{ my: 2, borderColor: '#E9ECEF' }} />

            <Typography sx={{ color: '#2C507A', fontWeight: 600, fontSize: '0.9rem' }}>
              Receipt #: {receipt.receiptNumber}
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              TXN #: {receipt.transactionNumber}
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              Date: {createdAt.toLocaleDateString()} {createdAt.toLocaleTimeString()}
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              Cashier: Self-Service Kiosk
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              POS Terminal: {receipt.posId || APP_CONFIG.POS_NUMBER}
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              Payment: {receipt.paymentMethod}
            </Typography>

            {appliedPromos.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {appliedPromos.map((p) => (
                  <Chip key={p} label={p} size="small" color="success" variant="outlined" />
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Items List */}
          <Typography
            variant="h5"
            sx={{
              color: '#2C507A',
              fontWeight: 600,
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Items Purchased
          </Typography>

          <List dense>
            {displayItems.map((item, index) => (
              <React.Fragment key={`${item.key}-${index}`}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1">{item.name}</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatPrice(item.lineTotal)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" />
                          <Typography variant="body2" color="text.secondary">
                            {item.qty} × {formatPrice(item.unitPrice)}
                          </Typography>
                        </Box>

                        {item.lineDiscount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#28a745' }}>
                              Discount
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#28a745', fontWeight: 600 }}>
                              -{formatPrice(item.lineDiscount)}
                            </Typography>
                          </Box>
                        )}

                        {item.appliedPromos.length > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Promo: {item.appliedPromos.map((p) => String(p).toUpperCase()).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
                {index < displayItems.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 2, borderColor: '#E9ECEF' }} />

          {/* ✅ REAL Totals */}
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#6C757D' }}>Subtotal:</Typography>
              <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>
                {formatPrice(totals.subtotalBefore)}
              </Typography>
            </Box>

            {totals.discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: '#6C757D' }}>Discount:</Typography>
                <Typography sx={{ color: '#28a745', fontWeight: 600 }}>
                  -{formatPrice(totals.discount)}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: '#6C757D' }}>Subtotal (After Discount):</Typography>
              <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>
                {formatPrice(totals.subtotalAfter)}
              </Typography>
            </Box>

            {totals.bottleDepositTotal > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: '#6C757D' }}>Bottle Deposit:</Typography>
                <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>
                  {formatPrice(totals.bottleDepositTotal)}
                </Typography>
              </Box>
            )}

            {totals.taxTotal > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: '#6C757D' }}>Tax:</Typography>
                <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>
                  {formatPrice(totals.taxTotal)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: '#E9ECEF' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" sx={{ color: '#2C507A', fontWeight: 700 }}>
                Total:
              </Typography>
              <Typography variant="h4" sx={{ color: '#2C507A', fontWeight: 700 }}>
                {formatPrice(totals.grandTotal)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: '#E9ECEF' }} />

          {/* Footer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem' }}>
              Thank you for shopping at Aisle 24!
            </Typography>
            <Typography sx={{ color: '#6C757D', fontSize: '0.9rem', mt: 1 }}>
              Keep this receipt for your records
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={ontarioTechStyles.receipt.actionButtons}>
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={handlePrint}
          sx={ontarioTechStyles.receipt.secondaryActionButton}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<Email />}
          onClick={() => handleEmailReceipt()}
          sx={ontarioTechStyles.receipt.secondaryActionButton}
        >
          Email
        </Button>
        <Button
          variant="contained"
          startIcon={<ShoppingCart />}
          onClick={onNewTransaction}
          sx={ontarioTechStyles.receipt.primaryActionButton}
        >
          New Transaction
        </Button>
      </Box>
    </Box>
  );
};

export default ReceiptInterface;